import { client } from '@/api/http';
import { type AxiosRequestConfig, type AxiosResponse, HttpStatusCode } from 'axios';
import { errorDetail } from '@/api/errorDetail';
import { XRAY_HIP_SPINE_VIEW_IDS } from '@/api/xrays';
import { AppError } from '@/stores/appErrorStore';
import { type SideId } from '@/domain/anatomy/side';
import { type LumbarLevel } from '@/domain/anatomy/hipSpineLandmarks';

export type HipSpinePostureApiObject = {
    id: number;

    femur_1_head_radius: number;
    femur_2_head_radius: number;
    original_femur_1_head_radius: number;
    original_femur_2_head_radius: number;

    lumbar_vertebra_end_plate: LumbarLevel;
    original_lumbar_vertebra_end_plate: LumbarLevel;

    file_name: string;
    has_unapproved_changes: boolean;
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];
    side: SideId;
    original_side: SideId;
};

/** Fetch a specific hip spine posture */
export async function getHipSpinePosture(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    config?: AxiosRequestConfig,
): Promise<HipSpinePostureApiObject> {
    const { status, data }: AxiosResponse = await client.get<HipSpinePostureApiObject>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}`,
        config,
    );

    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} not found` + errorDetail(data),
            'This Hip Spine posture does not exist or you do not have permission to view it.',
        );
    }

    throw new Error(`Failed to get hip spine ${posture}` + errorDetail(data));
}

/** Fetch all hip spine postures for a hip spine study */
export async function getAllHipSpinePostures(
    caseId: number,
    hipSpineStudyId: number,
    config?: AxiosRequestConfig,
): Promise<HipSpinePostureApiObject[]> {
    const { status, data }: AxiosResponse = await client.get<{
        postures: HipSpinePostureApiObject[];
    }>(`cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures`, config);

    if (status === HttpStatusCode.Ok) {
        return data.postures;
    } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine postures not found` + errorDetail(data),
            'These Hip Spine postures do not exist or you do not have permission to view them.',
        );
    }

    throw new Error(`Failed to get hip spine postures` + errorDetail(data));
}

export type HipSpinePostureUpdate = {
    side: SideId;
    lumbar_vertebra_end_plate: LumbarLevel;
    femur_1_head_radius: number;
    femur_2_head_radius: number;
};

export type PutHipSpinePostureArgs = {
    body: HipSpinePostureUpdate;
    config?: AxiosRequestConfig;
};

/** Save updated editable fields for a hip spine posture */
export async function putHipSpinePosture(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    { body, config }: PutHipSpinePostureArgs,
): Promise<HipSpinePostureApiObject> {
    const { status, data }: AxiosResponse = await client.put<HipSpinePostureApiObject>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}`,
        body,
        config,
    );

    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} posture update forbidden or not found` + errorDetail(data),
            `The Hip Spine ${posture} posture does not exist or you do not have permission to update it.`,
        );
    }

    throw new Error(`Failed to update hip spine ${posture} posture` + errorDetail(data));
}

/** Fetch hip spine x-ray file (PNG) for a posture, returns a blob URL */
export async function getHipSpineXrayFile(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    config?: AxiosRequestConfig,
): Promise<string> {
    const { status, data }: AxiosResponse = await client.get(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/files/${posture}`,
        {
            responseType: 'blob',
            ...config,
        },
    );
    if (status === HttpStatusCode.Ok) {
        return URL.createObjectURL(data);
    } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} file not found`,
            `The Hip Spine ${posture} file does not exist or you do not have permission to view it.`,
        );
    }

    throw new Error(`Failed to get hip spine ${posture} file`);
}

/** Reset a posture and all its landmarks to their AI originals, returning the updated posture */
export async function resetHipSpinePosture(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    config?: AxiosRequestConfig,
): Promise<HipSpinePostureApiObject> {
    const { status, data }: AxiosResponse = await client.post<HipSpinePostureApiObject>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}/reset`,
        undefined,
        config,
    );

    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} reset forbidden` + errorDetail(data),
            'You do not have permission to reset this posture.',
        );
    }

    throw new Error(`Failed to reset hip spine ${posture}` + errorDetail(data));
}
